import React, { useState, useEffect } from "react";
import NavBar from "../../../../layout/navbar/navbar";
import { Button, Alert, Container, Spinner, Modal } from "react-bootstrap";
import { IoMdInformationCircleOutline, IoMdClose } from "react-icons/io";
import { BiAnalyse } from "react-icons/bi";
import { AiOutlineDelete, AiOutlineCheckSquare } from "react-icons/ai";
import { MdRestore } from "react-icons/md";
import { useSupabaseApp } from "../../../../SupabaseApp";
// import { useHistory } from 'react-router-dom';

/**
 * ArchivePitchDeckAnalysis Component
 *
 * Manage archived pitch deck analysis documents. Displays list of archived pitch decks
 * with options to restore or permanently delete. Uses Realm functions to fetch archived
 * documents and handle restore/delete operations. Includes modals for confirmation before
 * restore or delete actions. Shows loading states and alert notifications for user feedback.
 * Requires user authentication. Implements document lifecycle management for cleanup
 * and recovery of archived analyses.
 *
 * @returns {JSX.Element} Page displaying archived pitch decks with restore/delete options
 */
const ArchivePitchDeckAnalysis = () => {
  // State to control alert component visibility
  const [alert, setAlert] = useState(true);

  // State to store alert message (initially shows loading message)
  const [message, setMessage] = useState(
    <div style={{ display: "flex", paddingTop: "10px" }}>
      <div style={{ width: "90%" }}>
        <p style={{ color: "#0f9d58" }}>
          <BiAnalyse className="alert-icon" /> Loading
        </p>
        <p style={{ marginLeft: "28px", fontSize: "14px" }}>
          Won't be long now. We're always doing our best to reduce waiting time.
        </p>
        <p style={{ marginLeft: "28px", fontSize: "14px" }}>
          If your waiting time is too long,{" "}
          <a
            href="https://www.roiquant.com/contact"
            target="_blank"
            rel="noopener noreferrer"
          >
            contact us
          </a>
          .
        </p>
      </div>
      <div style={{ width: "10%", textAlign: "right" }}>
        <Spinner animation="border" variant="secondary" />
      </div>
    </div>,
  );

  // Close the alert component and clear message
  function closeAlert() {
    setAlert(false);
    setMessage("");
  }

  // State to store the ID of pitch deck to restore
  const [pitchDeckId, setPitchDeckId] = useState();

  // State to control restore confirmation modal visibility
  const [restoreModal, setRestoreModal] = useState(false);

  // Open restore confirmation modal with selected pitch deck ID
  const openRestoreModal = (e) => {
    setPitchDeckId(e);
    setRestoreModal(true);
  };

  // Close restore confirmation modal
  function closeRestoreModal() {
    setPitchDeckId();
    setRestoreModal(false);
  }

  // State to control delete confirmation modal visibility
  const [deleteModal, setDeleteModal] = useState(false);

  // Open delete confirmation modal with selected pitch deck ID
  const openDeleteModal = (e) => {
    setPitchDeckId(e);
    setDeleteModal(true);
  };

  // Close delete confirmation modal
  function closeDeleteModal() {
    setPitchDeckId();
    setDeleteModal(false);
  }

  // Get current user from Supabase authentication
  const { supabase } = useSupabaseApp();

  // Realm functions to fetch and manage archived pitch decks
  //   const { getUsersDocuments, restorePitchDeck, deletePitchDeck } =
  //     currentUser.functions;

  // State to store list of archived pitch deck documents
  const [pitchDecks, setPitchDecks] = useState([]);

  /**
   * Fetch archived pitch deck documents on component mount
   * Updates pitchDecks state with list of user's archived documents
   */
  useEffect(() => {
    const getUsersDocuments = async () => {
      try {
        const { data, error } = await supabase.functions.invoke(
          "getUsersDocuments",
          {
            body: {
              feature: "Business plan builder",
              archive: true,
            },
          },
        );

        if (error) throw error;

        setPitchDecks(data);
        setAlert(false);
      } catch (err) {
        console.error("Failed to get pitch decks:", err);
      }
    };

    getUsersDocuments();
  }, [supabase]);

  /**
   * Restore archived pitch deck after user confirms action
   * Moves document from archive back to active collection
   */
  async function restorePitchDeckConfirmation() {
    // hide modal component
    closeRestoreModal();

    // show alert message
    setAlert(true);
    setMessage(
      <div style={{ display: "flex", paddingTop: "10px" }}>
        <div style={{ width: "90%" }}>
          <p style={{ color: "#0f9d58" }}>
            <BiAnalyse className="alert-icon" /> Restoring
          </p>
          <p style={{ marginLeft: "28px" }}>
            We'll notify you once your business plan is restored.
          </p>
        </div>
        <div style={{ width: "10%", textAlign: "right" }}>
          <Spinner animation="border" variant="secondary" />
        </div>
      </div>,
    );

    try {
      const { data, error } = await supabase.functions.invoke(
        "restorePitchDeck",
        {
          body: {
            pitchDeckId: pitchDeckId,
          },
        },
      );

      if (error) throw error;

      setPitchDecks(data);

      setAlert(true);
      setMessage(
        <div style={{ paddingTop: "10px" }}>
          <p style={{ color: "#5aa469" }}>
            <AiOutlineCheckSquare className="alert-icon" /> Restored
          </p>
          <p style={{ marginLeft: "28px" }}>Your business plan is restored.</p>
        </div>,
      );
    } catch (err) {
      // To-do: Alert user an error was returned
      // console.error(err);
    }

    // close alert
    closeAlert();
  }

  /**
   * Permanently delete archived pitch deck after user confirms action
   * Removes document from archive collection permanently
   */
  async function deletePitchDeckConfirmation() {
    // hide modal component
    closeDeleteModal();

    // show alert message
    setAlert(true);
    setMessage(
      <div style={{ display: "flex", paddingTop: "10px" }}>
        <div style={{ width: "90%" }}>
          <p style={{ color: "#0f9d58" }}>
            <BiAnalyse className="alert-icon" /> Loading
          </p>
          <p style={{ marginLeft: "28px", fontSize: "14px" }}>
            Won't be long now. We're always doing our best to reduce waiting
            time.
          </p>
          <p style={{ marginLeft: "28px", fontSize: "14px" }}>
            If your waiting time is too long,{" "}
            <a
              href="https://www.roiquant.com/contact"
              target="_blank"
              rel="noopener noreferrer"
            >
              contact us
            </a>
            .
          </p>
        </div>
        <div style={{ width: "10%", textAlign: "right" }}>
          <Spinner animation="border" variant="secondary" />
        </div>
      </div>,
    );

    try {
      const { data, error } = await supabase.functions.invoke(
        "deletePitchDeck",
        {
          body: {
            pitchDeckId: pitchDeckId,
          },
        },
      );

      if (error) throw error;

      setPitchDecks(data);
    } catch (err) {
      // To-do: Alert user an error was returned
      // console.error(err);
    }

    // close alert
    closeAlert();
  }

  return (
    <>
      {/* navbar */}
      <NavBar />

      {/* alert message */}
      {alert ? (
        <Alert className="error-message floating-alert" variant="light">
          <div>
            <div style={{ textAlign: "right" }}>
              <Button className="pitchdeckmessageclose" onClick={closeAlert}>
                <IoMdClose />
              </Button>
            </div>
            {message}
          </div>
        </Alert>
      ) : (
        ""
      )}

      <div id="pitchdeck">
        <Container className="col-11" style={{ paddingBottom: "200px" }}>
          <div className="pitchdeckheaderdiv">
            {/* title */}
            <div className="pitchdecktitlediv">
              <br />
              <span className="pitchdecktext">
                Archive
                <sup data-tip="">
                  <IoMdInformationCircleOutline id="information-icon" />
                </sup>
              </span>
            </div>

            {/* buttons */}
            <div
              className="pitchdeckstatsdiv"
              style={{ display: "flex", justifyContent: "end" }}
            >
              <a href="/tools/founders/business-plan">
                <Button className="pitchdeckaddbutton glintanimation">
                  <span>Back</span>
                </Button>
              </a>
            </div>
          </div>

          {/* table */}
          <div>
            <table className="pitchdeckhistorytable">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Modified</th>
                  <th>Restore</th>
                  <th>Delete</th>
                </tr>
              </thead>
              <tbody>
                {pitchDecks?.length === 0 ? (
                  <tr className="pitchdecktablerow">
                    <td className="pitchdecktabledata">-</td>
                    <td className="pitchdecktabledata">-</td>
                    <td className="pitchdecktabledata">-</td>
                    <td className="pitchdecktabledata">-</td>
                  </tr>
                ) : (
                  pitchDecks?.map((pitchDeck, index) => (
                    <tr key={index} className="pitchdecktablerow">
                      <td className="pitchdecktabledata">
                        {pitchDeck.name
                          ? pitchDeck.name
                          : "Untitled business plan"}
                      </td>
                      <td className="pitchdecktabledata">
                        {pitchDeck.lastModified
                          ? pitchDeck.lastModified.substring(0, 10)
                          : "-"}
                      </td>
                      <td className="pitchdecktabledata">
                        <Button
                          className="edit-button"
                          onClick={() =>
                            openRestoreModal(pitchDeck.pitchDeckId)
                          }
                        >
                          <MdRestore />
                        </Button>
                      </td>
                      <td className="pitchdecktabledata">
                        <Button
                          className="delete-button"
                          onClick={() => openDeleteModal(pitchDeck.pitchDeckId)}
                        >
                          <AiOutlineDelete />
                        </Button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </Container>
      </div>

      {/* restore modal */}
      <Modal
        className="modal-div"
        show={restoreModal}
        onHide={closeRestoreModal}
        fullscreen="sm-down"
        aria-labelledby="contained-modal-title-vcenter"
        centered
      >
        <Modal.Header closeButton style={{ borderBottom: "none" }} />
        <Modal.Body>
          <p style={{ textAlign: "center" }}>
            Are you sure you want to restore this business plan?
          </p>

          <div style={{ textAlign: "center" }}>
            <button className="cancelbutton" onClick={closeRestoreModal}>
              Cancel
            </button>

            <button
              className="modal-compare glintanimation"
              onClick={restorePitchDeckConfirmation}
            >
              Restore
            </button>
          </div>
        </Modal.Body>
      </Modal>

      {/* delete modal */}
      <Modal
        className="modal-div"
        show={deleteModal}
        onHide={closeDeleteModal}
        fullscreen="sm-down"
        aria-labelledby="contained-modal-title-vcenter"
        centered
      >
        <Modal.Header closeButton style={{ borderBottom: "none" }} />
        <Modal.Body>
          <p style={{ textAlign: "center" }}>
            Are you sure you want to delete this business plan?
          </p>

          <div style={{ textAlign: "center" }}>
            <button className="cancelbutton" onClick={closeDeleteModal}>
              Cancel
            </button>

            <button
              className="modal-delete glintanimation"
              onClick={deletePitchDeckConfirmation}
            >
              Delete
            </button>
          </div>
        </Modal.Body>
      </Modal>
    </>
  );
};

export default ArchivePitchDeckAnalysis;
